import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';
import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import { UserListCustomCellTemplateComponent } from '../../components/user-list-custom-cell-template/user-list-custom-cell-template.component';
import { Observable, of } from 'rxjs';
import { Route, RouteParam } from '@web/common';
import { RoleProviderService } from '@web/shared/module/auth';
import { UserListItemAction } from '../../enums/user-list-item-action';
import { UserCollectionItem } from '../../models/user-collection-item';
import { UserCollectionFilter } from '../../models/user-collection-filter';
import { UserListCollectionAction } from '../../enums/user-list-collection-action';
import { UserListAction } from '../../enums/user-list-action';
import { CardModule } from 'primeng/card';
import { UserDataSourceService } from '../../services/user-data-source.service';
import { UserStatusProviderService } from '../../providers/user-status.provider.service';
import { once } from '@web/utils';
import { AccountTypeProviderService } from '../../providers/account-type-provider.service';

@Component({
  selector: 'app-user-list',
  imports: [TableComponent, UserListCustomCellTemplateComponent, CardModule],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<UserCollectionItem, UserCollectionFilter, UserListItemAction, UserListCollectionAction, UserListAction>).create();
  private readonly userDataSource = inject(UserDataSourceService);
  private readonly roleProvider = inject(RoleProviderService);
  private readonly userStatusProvider = inject(UserStatusProviderService);
  private readonly accountTypeProvider = inject(AccountTypeProviderService);
  private readonly table = inject(TableService);

  public readonly addUserVisibility = input<boolean>(false);

  protected config!: TableConfig<
    UserCollectionItem,
    UserCollectionFilter,
    UserListItemAction,
    UserListCollectionAction,
    UserListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .actions((builder) => [
        builder
          .initRouterLink(UserListAction.Create)
          .label('Dodaj użytkownika')
          .visibility(() => of(this.addUserVisibility()))
          .routerLink((provider) => provider.routerLink(Route.UserCreate))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(UserListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) =>
              ({ id }) =>
                provider.routerLink(Route.UserDetails, {
                  [RouteParam.Guid]: id,
                }),
          )
          .build(),

        builder
          .initRouterLink(UserListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.UserEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initMore(UserListItemAction.More)
          .visibility((item) => this.shouldVisibleMore(item))
          .children((builder) => [
            builder
              .initCommand(UserListItemAction.Remove)
              .visibility((item) => item.canBeRemoved)
              .icon(Icon.Trash)
              .tooltip('Usuń')
              .command((item) => this.remove(item))
              .build(),

            builder
              .initCommand(UserListItemAction.Activate)
              .visibility((item) => item.canBeActivated)
              .icon(Icon.CheckCircle)
              .tooltip('Aktywuj')
              .command((item) => this.activate(item))
              .build(),

            builder
              .initCommand(UserListItemAction.ResendActivationMessage)
              .visibility((item) => item.canActivationMessageBeResent)
              .icon(Icon.SendArrowDown)
              .tooltip('Wyślij e-mail aktywacyjny')
              .command((item) => this.resendActivationMessage(item))
              .build(),
          ])
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('username')
          .build(),

        builder //
          .initText('email')
          .build(),

        builder //
          .initText('firstName')
          .build(),

        builder //
          .initText('lastName')
          .build(),

        builder //
          .initText('displayName')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder //
          .initDateRange('activatedAt')
          .build(),

        builder //
          .initDateRange('lastLoginAt')
          .build(),

        builder //
          .initMultiSelect('roles')
          .dictionary(() => this.roleProvider.selectItems())
          .build(),

        builder //
          .initMultiSelect('accountType')
          .dictionary(() => this.accountTypeProvider.selectItems())
          .build(),

        builder //
          .initMultiSelect('status')
          .dictionary(() => this.userStatusProvider.selectItems())
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initSelection()
          .build(),

        builder //
          .initText('username')
          .label('Nazwa użytkownika')
          .sortable(true)
          .sortKey('username')
          .filterable(true)
          .filterKey('username')
          .build(),

        builder //
          .initText('email')
          .label('Email')
          .sortable(true)
          .sortKey('email')
          .filterable(true)
          .filterKey('email')
          .build(),

        builder //
          .initText('firstName')
          .label('Imię')
          .sortable(true)
          .sortKey('firstName')
          .filterable(true)
          .filterKey('firstName')
          .build(),

        builder //
          .initText('lastName')
          .label('Nazwisko')
          .sortable(true)
          .sortKey('lastName')
          .filterable(true)
          .filterKey('lastName')
          .build(),

        builder //
          .initText('displayName')
          .label('Wyświetlana nazwa')
          .width('12rem')
          .sortable(true)
          .sortKey('displayName')
          .filterable(true)
          .filterKey('displayName')
          .build(),

        builder //
          .initCustom('accountType')
          .width('14rem')
          .label('Rodzaj')
          .sortable(true)
          .sortKey('accountType')
          .filterable(true)
          .filterKey('accountType')
          .build(),

        builder //
          .initCustom('status')
          .width('8rem')
          .label('Status')
          .sortable(true)
          .sortKey('status')
          .filterable(true)
          .filterKey('status')
          .build(),

        builder //
          .initCustom('roles')
          .width('25rem')
          .label('Role użytkownika')
          .sortable(true)
          .sortKey('roles')
          .filterable(true)
          .filterKey('roles')
          .build(),

        builder //
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),

        builder //
          .initDateTime('activatedAt')
          .label('Data aktywacji')
          .sortable(true)
          .sortKey('activatedAt')
          .filterable(true)
          .filterKey('activatedAt')
          .build(),

        builder //
          .initDateTime('lastLoginAt')
          .label('Data ostatniego logowania')
          .sortable(true)
          .sortKey('lastLoginAt')
          .filterable(true)
          .filterKey('lastLoginAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<UserCollectionFilter>,
        ): Observable<TableDataPayload<UserCollectionItem>> =>
          this.userDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private shouldVisibleMore(item: UserCollectionItem): boolean {
    return [
      item.canActivationMessageBeResent,
      item.canBeActivated,
      item.canBeRemoved,
    ].some((value) => value);
  }

  private remove(item: UserCollectionItem): void {
    this.userDataSource
      .removeById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private resendActivationMessage(item: UserCollectionItem): void {
    this.userDataSource
      .resendActivationMessageById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private activate(item: UserCollectionItem): void {
    this.userDataSource
      .activate(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.table.refresh();
  }
}
